// src/components/NodeApplicationWithModals.jsx

import React, { useState, useEffect } from 'react';
import NodeApplication from './NodeApplication';
import NodeManagementModals from './NodeManagementModals';
import { useWallet } from '../context/WalletContext';
import * as api from '../services/api';
import '../styles/NodeApplicationWithModals.css';

/**
 * Enhanced NodeApplication component that includes management modals
 * This wrapper component allows us to add the multi-node management modals 
 * without modifying the original NodeApplication component
 */
const NodeApplicationWithModals = () => {
  const { wallet } = useWallet();
  const [activatedNodes, setActivatedNodes] = useState([]);
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [loadError, setLoadError] = useState('');
  
  // Check if user has any activated nodes whenever wallet changes
  useEffect(() => {
    if (wallet.connected) {
      checkUserNodes();
    } else {
      setActivatedNodes([]);
    }
  }, [wallet.connected, wallet.address]);
  
  // Clean up modal-related classes when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);
  
  const checkUserNodes = async () => {
    if (!wallet.connected) return;
    
    try {
      setIsLoadingNodes(true);
      setLoadError('');
      
      // Use the correct blockchain network ID from your configuration
      const networkId = 1; // Replace with dynamic value if needed
      
      // Get signature message for authentication
      const messageResponse = await api.generateSignatureMessage(
        wallet.address,
        networkId
      );
      
      if (!messageResponse || !messageResponse.message) {
        throw new Error('Invalid signature message response');
      }
      
      // Sign message with wallet
      const signature = await api.signMessage(
        wallet.type,
        wallet.address,
        messageResponse.message
      );
      
      // Get user's node applications with the signature
      const applications = await api.getWalletApplications(
        wallet.address,
        signature,
        messageResponse.message,
        networkId,
        wallet.type
      );
      
      if (applications && applications.applications) {
        // Filter to only include activated nodes according to your business logic
        const activeNodes = applications.applications.filter(app => 
          app.status === 'active' || app.status === 'approved'
        );
        
        setActivatedNodes(activeNodes);
        console.log(`Found ${activeNodes.length} activated nodes`);
      } else {
        console.log("No activated nodes found or API returned unexpected format");
        setActivatedNodes([]);
      }
    } catch (err) {
      console.error('Error checking nodes:', err);
      setLoadError('Failed to load your nodes. Please try again later.');
      setActivatedNodes([]);
    } finally {
      setIsLoadingNodes(false);
    }
  };
  
  return (
    <section className="node-application" id="node-application">
      <div className="container">
        
        {/* Display node count if user has active nodes */}
        {activatedNodes.length > 0 && (
          <div className="active-nodes-info">
            <div className="node-count-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              You have {activatedNodes.length} activated {activatedNodes.length === 1 ? 'node' : 'nodes'}
            </div>
            <p className="node-info-text">
              Use the management buttons below to view your node invitation codes, manage your downline network, 
              and establish relationships with superior nodes.
            </p>
          </div>
        )}
        
        {/* Loading state for nodes */}
        {isLoadingNodes && wallet.connected && (
          <div className="loading-nodes">
            <div className="spinner small"></div>
            <span>Loading your nodes...</span>
          </div>
        )}
        
        {/* Error state for nodes */}
        {loadError && wallet.connected && (
          <div className="error-message node-load-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>{loadError}</span>
            <button className="retry-btn" onClick={checkUserNodes}>Retry</button>
          </div>
        )}
        

        {/* Original NodeApplication Component */}
        <NodeApplication />
      </div>
    </section>
  );
};

export default NodeApplicationWithModals;
